import { get, writable } from "svelte/store";
import GeoHelper from "../../Helpers/GeoHelper";
import MapStore from "../../Map/Store";

import moment from "moment";
import API from "../../API/API";
import LoadState from "../../Enums/LoadState";
import MapLayerGroup from "../../Map/Enums/MapLayerGroup";
import MapLayerManager from "../../Map/MapLayerManager";
import PopupStore from "../../Map/Popup/PopupStore";

const RiskIntStore = {
	meta: {
		visible: writable(false),
		loadState: writable(LoadState.INIT),
		error: writable(""),
		lastUpdated: writable(Date.now()),
		layerName: "riskint_incidents",
		initialised: writable(false),

		incidentVisible: writable(false),
		selectedIncident: writable(null)
	},
	results: {
		incidents: writable([]),
		incidentTypes: writable([]),
		incidentTypesById: writable({}),
		regions: writable([]),
		geojson: writable(GeoHelper.emptyGeoJson())
	},
	helpers: {
		findIncidentById: (id) => {
			const incidents = get(RiskIntStore.results.incidents);
			for (let i = 0; i < incidents.length; i++) {
				if (incidents[i].id === id) {
					return incidents[i];
				}
			}
			return null;
		},
		getIconSrcForIncidentTypeId: (incidentTypeId, iconType = "round") => {
			const incidentTypes = get(RiskIntStore.results.incidentTypesById);
			const externalFullFilename = incidentTypes[incidentTypeId].group.icons[iconType];
			const shortFilename = externalFullFilename.substr(externalFullFilename.lastIndexOf("/") + 1);
			return "/img/ri/" + shortFilename;
		}
	},
	actions: {
		init: (map) => {
			RiskIntStore.meta.error.set("");
			RiskIntStore.meta.loadState.set(LoadState.LOADING);

			API.get("riskint")
				.then((result) => {
					RiskIntStore.results.incidentTypes.set(result.incident_types);
					RiskIntStore.results.regions.set(result.regions);
					RiskIntStore.results.incidents.set(result.incidents);

					const incidentTypesById = {};
					for (let i = 0; i < result.incident_types.length; i++) {
						incidentTypesById[result.incident_types[i].id] = result.incident_types[i];
					}
					RiskIntStore.results.incidentTypesById.set(incidentTypesById);

					let toLoad = 0;
					for (let i = 0; i < result.incident_types.length; i++) {
						const typeId = result.incident_types[i].id;
						const markerUrl = RiskIntStore.helpers.getIconSrcForIncidentTypeId(typeId, "marker");
						const imageId = "marker-RI-incidentType-" + typeId;
						if (!map.hasImage(imageId)) {
							toLoad++;
							map.loadImage(markerUrl, (error, image) => {
								if (error) {
									toLoad--;
									console.warn("could not load ri marker icon", error);
									throw error;
								}
								map.addImage("marker-RI-incidentType-" + typeId, image);
								toLoad--;
								if (toLoad === 0) {
									RiskIntStore.meta.error.set("");
									RiskIntStore.meta.loadState.set(LoadState.LOADED);
									RiskIntStore.meta.lastUpdated.set(Date.now());

									RiskIntStore.actions.updateFeatureCollection();
								}
							});
						}
					}

					// RiskIntStore.actions.updateFeatureCollection();

					RiskIntStore.meta.error.set("");
					RiskIntStore.meta.loadState.set(LoadState.LOADED);
				})
				.catch((e) => {
					console.error(e);
					RiskIntStore.meta.error.set(e.data.detail.message);
					RiskIntStore.meta.loadState.set(LoadState.INIT);
				});
		},
		openIncident: (incidentId) => {
			const incident = RiskIntStore.helpers.findIncidentById(incidentId);
			if (incident !== null) {
				RiskIntStore.meta.selectedIncident.set(incident);
				RiskIntStore.meta.incidentVisible.set(true);
			}
		},
		updateFeatureCollection: () => {
			const incidents = get(RiskIntStore.results.incidents);
			const features = [];
			for (let i = 0; i < incidents.length; i++) {
				const incident = incidents[i];
				const feature = GeoHelper.featurePoint([incident.markerPosition.longitude, incident.markerPosition.latitude]);
				feature.properties = incident;
				feature.properties.iconImage = "marker-RI-incidentType-" + incident.type.id;
				features.push(feature);
			}
			const geojson = GeoHelper.emptyGeoJson(features);
			RiskIntStore.results.geojson.set(geojson);

			if (get(RiskIntStore.meta.initialised)) {
				const map = MapStore.helpers.getMap();
				const idName = RiskIntStore.meta.layerName;
				map.getSource(idName).setData(geojson);
			}
		},
		initMap: (map) => {
			const idName = RiskIntStore.meta.layerName;
			if (typeof map.getSource(idName) === "undefined") {
				map.addSource(idName, {
					type: "geojson",
					data: GeoHelper.emptyGeoJson()
				});

				map.addLayer({
					id: idName,
					source: idName,
					type: "symbol",
					layout: {
						"icon-image": ["get", "iconImage"],
						"icon-allow-overlap": true,
						"icon-size": 0.5,
						"icon-anchor": "bottom"
					}
					// paint: {
					// 	"circle-radius": 5,
					// 	"circle-color": "rgba(0, 0, 0, 0.35)",
					// 	"circle-stroke-color": "rgba(0, 0, 0, 1.0)",
					// 	"circle-stroke-width": 1.5
					// }
				});
				MapLayerManager.addLayerGroup(MapLayerGroup.RISKINT_INCIDENTS, [idName], [idName]);

				const onLayerClick = (e) => {
					e.preventDefault();

					if (e.features.length > 0) {
						const feature = e.features[0];
						const incidentId = feature.properties.id;
						RiskIntStore.actions.openIncident(incidentId);
					}
				};

				const onLayerEnter = (e) => {
					map.getCanvas().style.cursor = "pointer";
					if (e.features.length > 0) {
						const coords = [e.lngLat.lng, e.lngLat.lat];
						let html = "";
						html += `<strong>${e.features[0].properties.title}</strong><br/><i>` + moment(e.features[0].properties.date).format("Do MMMM YYYY") + "</i><br/>Click to view!";
						PopupStore.actions.customHtml(html, 400, coords, true);
					}
				};
				const onLayerLeave = (e) => {
					map.getCanvas().style.cursor = MapStore.helpers.getDefaultCursor();
					PopupStore.actions.custom({}, null, false);
				};

				map.on("click", idName, onLayerClick);
				map.on("mouseenter", idName, onLayerEnter);
				map.on("mousemove", idName, onLayerEnter);
				map.on("mouseleave", idName, onLayerLeave);

				RiskIntStore.meta.initialised.set(true);
			}
		},

		deinitMap: () => {
			MapLayerManager.removeLayerGroup(MapLayerGroup.RISKINT_INCIDENTS);
			RiskIntStore.meta.initialised.set(false);
		}

	}
};
export default RiskIntStore;
